import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../common/state/hooks";
import { currentOrgSelector } from "../../../common/state/selectors/authSelector";
import {
  selectCachedForOrgId,
  selectCachedModulesForParcoursId,
  setCachedOrgId,
  setCachedModulesParcoursId,
  invalidateCreatorStatsCache,
  invalidateCreatorModulesCache,
} from "../state/statsNewSlice";

/**
 * Hook to manage creator stats cache based on current organization.
 * Invalidates cache when org changes and tracks if data should be refetched.
 * Also triggers refetch when page regains focus (visibility change).
 *
 * @returns {object} - { shouldRefetch, markAsFetched }
 *   - shouldRefetch: true if data should be fetched (org changed or no cache)
 *   - markAsFetched: call this after successful fetch to update cache metadata
 */
export const useCreatorStatsCache = () => {
  const dispatch = useAppDispatch();
  const currentOrg = useSelector(currentOrgSelector);
  const cachedForOrgId = useSelector(selectCachedForOrgId);
  const hasInvalidatedRef = useRef(false);
  const [visibilityRefetchTrigger, setVisibilityRefetchTrigger] = useState(0);

  const currentOrgId = currentOrg?.id ?? null;

  // Check if we need to invalidate cache (org changed)
  useEffect(() => {
    if (currentOrgId && cachedForOrgId && currentOrgId !== cachedForOrgId) {
      // Org changed, invalidate cache
      if (!hasInvalidatedRef.current) {
        hasInvalidatedRef.current = true;
        dispatch(invalidateCreatorStatsCache());
      }
    } else {
      hasInvalidatedRef.current = false;
    }
  }, [currentOrgId, cachedForOrgId, dispatch]);

  // Refetch when page regains focus (user comes back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Trigger refetch by incrementing the trigger
        setVisibilityRefetchTrigger((prev) => prev + 1);
        // Also invalidate modules cache so they refresh too
        dispatch(invalidateCreatorModulesCache());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  // Should refetch if:
  // 1. No cached org ID (first load)
  // 2. Current org doesn't match cached org
  // 3. Visibility refetch was triggered (page regained focus)
  const shouldRefetch =
    !cachedForOrgId ||
    (currentOrgId !== null && currentOrgId !== cachedForOrgId) ||
    visibilityRefetchTrigger > 0;

  // Function to call after successful fetch to update cache metadata
  // Memoized to prevent infinite loops in useEffect dependencies
  const markAsFetched = useCallback(() => {
    if (currentOrgId) {
      dispatch(setCachedOrgId(currentOrgId));
    }
    // Reset visibility trigger after fetch
    setVisibilityRefetchTrigger(0);
  }, [currentOrgId, dispatch]);

  return { shouldRefetch, markAsFetched, currentOrgId, cachedForOrgId };
};

/**
 * Hook to manage modules cache for a specific parcoursId.
 * Only refetches if parcoursId changed or cache was invalidated (visibility change).
 *
 * @param parcoursId - The parcoursId to fetch modules for
 * @returns {object} - { shouldRefetchModules, markModulesAsFetched }
 */
export const useCreatorModulesCache = (parcoursId: number | undefined) => {
  const dispatch = useAppDispatch();
  const cachedModulesForParcoursId = useSelector(selectCachedModulesForParcoursId);

  // Should refetch if:
  // 1. parcoursId is defined AND
  // 2. Either no cached parcoursId OR cached parcoursId is different
  const shouldRefetchModules =
    parcoursId !== undefined &&
    (cachedModulesForParcoursId === null || cachedModulesForParcoursId !== parcoursId);

  // Function to call after successful fetch
  const markModulesAsFetched = useCallback(() => {
    if (parcoursId !== undefined) {
      dispatch(setCachedModulesParcoursId(parcoursId));
    }
  }, [parcoursId, dispatch]);

  return { shouldRefetchModules, markModulesAsFetched };
};
